import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  readStoredActiveTenantId,
  setRuntimeActiveTenantId,
  writeStoredActiveTenantId,
} from '@/lib/activeTenantStorage';
import { api } from '@/store/api';
import { useAppDispatch } from '@/store/hooks';
import {
  useGetActiveTenantQuery,
  useListPlatformTenantsQuery,
  useMeQuery,
  useSetActiveTenantMutation,
} from '@/store/api/authApi';

export function useActiveTenant() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: me } = useMeQuery();
  const isPlatformSuperadmin = me?.isPlatformSuperadmin === true;
  const { data: backendActive, refetch } = useGetActiveTenantQuery(undefined, {
    skip: !isPlatformSuperadmin,
  });
  const { data: tenants = [] } = useListPlatformTenantsQuery(undefined, {
    skip: !isPlatformSuperadmin,
  });
  const [setActiveTenant] = useSetActiveTenantMutation();
  const [localTenantId, setLocalTenantId] = useState<number | null>(null);

  useEffect(() => {
    if (!me?.id) {
      setLocalTenantId(null);
      setRuntimeActiveTenantId(null);
      return;
    }
    const stored = readStoredActiveTenantId(me.id);
    const resolved = backendActive?.activeTenantId ?? stored ?? me.tenantId;
    setLocalTenantId(resolved);
    setRuntimeActiveTenantId(resolved);
    if (stored !== resolved) {
      writeStoredActiveTenantId(me.id, resolved);
    }
  }, [me?.id, me?.tenantId, backendActive?.activeTenantId]);

  const changeTenant = useCallback(async (tenantId: number) => {
    if (!me?.id || !isPlatformSuperadmin) {
      return;
    }
    await setActiveTenant({ tenantId }).unwrap();
    writeStoredActiveTenantId(me.id, tenantId);
    setRuntimeActiveTenantId(tenantId);
    setLocalTenantId(tenantId);
    dispatch(api.util.resetApiState());
    await refetch();
    navigate('/');
  }, [dispatch, isPlatformSuperadmin, me?.id, navigate, refetch, setActiveTenant]);

  const activeTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === localTenantId) ?? null,
    [localTenantId, tenants],
  );

  return {
    isPlatformSuperadmin,
    activeTenantId: localTenantId,
    activeTenant,
    tenants,
    changeTenant,
    needsTenantSelection: isPlatformSuperadmin && tenants.length > 1 && !localTenantId,
  };
}
